import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  handleSpringDataMutationRequest,
  handleSpringDataRequest,
} from "../../utils/apiUtils";
import { toast } from "react-toastify";

const TOPAZ_PROVIDER = "TOPAZ_SIGPLUSEXTLITE";
const DEFAULT_TOPAZ_PROMPT_TEXT =
  "By signing, you have acknowledged and agreed with the terms and conditions set forth by the property.";
const DEFAULT_SUPPORTED_MODELS = [
  "T-LBK462-HSB-R",
  "TOPAZ_MODEL_15",
  "T-LBK460-HSB-R",
];

const buildDefaultConfig = () => ({
  enabled: true,
  sdk: "SIGPLUSEXTLITE",
  display_name: "Topaz SigPlusExtLite",
  capture_mode: "SIGNATURE_ONLY",
  signed_pdf_mode: "SERVER_GENERATED_REGISTRATION_CARD",
  prompt_text: DEFAULT_TOPAZ_PROMPT_TEXT,
  supported_models: DEFAULT_SUPPORTED_MODELS,
});

const isUnitedStatesHotel = (country) =>
  [
    "UNITED STATES",
    "UNITED STATES OF AMERICA",
    "USA",
    "US",
  ].includes((country || "").trim().toUpperCase());

export default function TopazSignaturePadConfig({
  hotelId,
  hotelCountry,
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [promptText, setPromptText] = useState(DEFAULT_TOPAZ_PROMPT_TEXT);
  const [currentProvider, setCurrentProvider] = useState("NONE");

  const shouldShow = useMemo(
    () => isUnitedStatesHotel(hotelCountry),
    [hotelCountry]
  );

  const applyConfig = (deviceConfig = {}) => {
    const defaultConfig = buildDefaultConfig();
    setEnabled(deviceConfig?.enabled ?? defaultConfig.enabled);
    setPromptText(deviceConfig?.prompt_text || defaultConfig.prompt_text);
  };

  const getTopazConfig = useCallback(() => {
    if (!hotelId || !shouldShow) {
      setLoading(false);
      return;
    }

    setLoading(true);
    handleSpringDataRequest(
      `core/api/v1/signature-device/get-config?hotelId=${hotelId}`
    )
      .then((data) => {
        const provider = data?.signature_device_name || "NONE";
        setCurrentProvider(provider);

        if (provider === TOPAZ_PROVIDER && data?.configured) {
          applyConfig(data?.device_config || {});
          return;
        }

        applyConfig(buildDefaultConfig());
      })
      .catch((err) => {
        console.log(err);
        applyConfig(buildDefaultConfig());
        setCurrentProvider("NONE");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [hotelId, shouldShow]);

  useEffect(() => {
    getTopazConfig();
  }, [getTopazConfig]);

  const handleSave = () => {
    setSaving(true);
    const payload = {
      signature_device_name: TOPAZ_PROVIDER,
      device_config: {
        enabled,
        sdk: "SIGPLUSEXTLITE",
        display_name: "Topaz SigPlusExtLite",
        capture_mode: "SIGNATURE_ONLY",
        signed_pdf_mode: "SERVER_GENERATED_REGISTRATION_CARD",
        prompt_text: promptText?.trim() || DEFAULT_TOPAZ_PROMPT_TEXT,
        supported_models: DEFAULT_SUPPORTED_MODELS,
      },
    };

    handleSpringDataMutationRequest(
      "POST",
      `core/api/v1/signature-device/upsert-config?hotelId=${hotelId}`,
      payload
    )
      .then(() => {
        toast.success("Topaz signature pad config saved successfully.");
        setCurrentProvider(TOPAZ_PROVIDER);
        getTopazConfig();
      })
      .catch((err) => {
        console.log(err);
        toast.error(
          err?.response?.data?.error ||
            err?.response?.data ||
            "Unable to save Topaz signature pad config."
        );
      })
      .finally(() => {
        setSaving(false);
      });
  };

  if (!shouldShow) {
    return null;
  }

  return (
    <Grid item xs={12} sx={{ boxShadow: 3 }}>
      <Card sx={{ marginBottom: 2 }}>
        <CardHeader
          title={"Configure Topaz Signature Pad"}
          subheader={
            "Used for PMS frontdesk signature capture. When enabled, this overrides payment-device signature capture for the property."
          }
        />
        <Divider />
        <CardContent>
          {loading ? (
            <Box
              sx={{ minHeight: 120, display: "flex", alignItems: "center" }}
            >
              <CircularProgress size={22} />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {currentProvider !== "NONE" && currentProvider !== TOPAZ_PROVIDER ? (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    Current signature device provider is {currentProvider}.
                    Saving here will switch the property to Topaz.
                  </Alert>
                </Grid>
              ) : null}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={enabled}
                      onChange={(e) => setEnabled(e.target.checked)}
                    />
                  }
                  label="Enable Topaz signature capture for this property"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Prompt Text"
                  multiline
                  minRows={3}
                  fullWidth
                  value={promptText}
                  onChange={(e) => setPromptText(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Supported Models"
                  fullWidth
                  value={DEFAULT_SUPPORTED_MODELS.join(", ")}
                  InputProps={{ readOnly: true }}
                  helperText="These models are saved by default for every Topaz property config."
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Provider: {TOPAZ_PROVIDER}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="custom-button"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? <CircularProgress size={20} /> : "Save Topaz Config"}
                </Button>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
}
